import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { type SortingState } from '@tanstack/react-table';
import { Link } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import {
  createDivisionOrdersSearchAtom,
  type DivisionOrdersSearchParams,
} from 'pages/division-orders/list/atoms';
import type { DivisionOrderResult } from 'pages/division-orders/list/types';
import { SORT_FIELD_MAP } from 'pages/division-orders/list/utils';
import { EntityResponse } from 'types/api-responses';

type DORow = DivisionOrderResult & Record<string, unknown>;

const columns: DataTableColumnDef<DORow>[] = [
  {
    accessorKey: 'name',
    header: 'Division Order',
    size: 180,
    cell: ({ row }) => <Link to={row.original.href}>{row.original.name}</Link>,
  },
  {
    id: 'lineItemName',
    accessorFn: (row) => row.lineItems.map((li) => li.name).join(', '),
    header: 'DO Line Item Name',
    size: 200,
    cell: ({ row }) => {
      const items = row.original.lineItems;
      if (items.length === 0) return '--';
      return (
        <div>
          {items.map((li) => (
            <Link key={li.id} to={li.href}>
              {li.name}
            </Link>
          ))}
        </div>
      );
    },
  },
  {
    id: 'uwi',
    accessorFn: (row) =>
      row.lineItems
        .map((li) => li.uwi)
        .filter(Boolean)
        .join(', '),
    header: 'UWI',
    size: 140,
  },
  {
    accessorKey: 'dateReceived',
    header: 'Date Received',
    size: 140,
    meta: { type: 'date' },
  },
  {
    id: 'interestType',
    accessorFn: (row) => row.lineItems[0]?.interestType ?? null,
    header: 'Interest Type',
    size: 140,
  },
  {
    id: 'lineItemStatus',
    accessorFn: (row) => row.lineItems[0]?.lineItemStatus ?? null,
    header: 'Line Item Status',
    size: 150,
  },
  {
    id: 'operatorName',
    accessorFn: (row) => row.operator?.name ?? null,
    header: 'Operator',
    size: 200,
  },
];

const EntityDivisionOrders: FC<{ entity: EntityResponse }> = ({ entity }) => {
  const { searchAtom } = useMemo(() => createDivisionOrdersSearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const defaultParams: DivisionOrdersSearchParams = {
    page: 1,
    pageSize: 100,
    sortAttribute: 'dateReceived',
    sortDirection: 'DESC',
    entityId: entity.id,
  };

  const [params, setParams] =
    useState<DivisionOrdersSearchParams>(defaultParams);

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const updateParams = useCallback(
    (updates: Partial<DivisionOrdersSearchParams>) => {
      setParams((current) => ({ ...current, page: 1, ...updates }));
    },
    []
  );

  const handleSortChange = useCallback(
    (sorting: SortingState) => {
      if (sorting.length === 0) {
        updateParams({ sortAttribute: 'dateReceived', sortDirection: 'DESC' });
      } else {
        const sort = sorting[0];
        updateParams({
          sortAttribute: SORT_FIELD_MAP[sort.id] ?? sort.id,
          sortDirection: sort.desc ? 'DESC' : 'ASC',
        });
      }
    },
    [updateParams]
  );

  const handleGlobalSearchChange = useCallback(
    (search: string) => {
      const newText = search || undefined;
      if (params.text === newText) return;
      updateParams({ text: newText });
    },
    [params.text, updateParams]
  );

  const handleLoadData = useCallback(({ page, pageSize }: TableState) => {
    setParams((current) => ({ ...current, page, pageSize }));
  }, []);

  const results = searchResult.data?.results ?? [];
  const totalCount = searchResult.data?.totalCount ?? 0;

  return (
    <DataTable<DORow>
      data={results as DORow[]}
      columns={columns as DataTableColumnDef<DORow>[]}
      getRowId={(row) => String(row.id)}
      initialTableState={{ sortColumn: 'dateReceived', sortDirection: 'desc' }}
      persistenceKey={`entity-${entity.id}-division-orders-table`}
      config={{ columnFilters: false, columnReordering: false }}
      isLoading={searchResult.loading}
      error={searchResult.error ?? null}
      scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
      onPaginationChange={handleLoadData}
      onSortChange={(s) => handleSortChange(s.sorting)}
      onGlobalSearchChange={(s) => handleGlobalSearchChange(s.globalFilter)}
      searchPlaceholder="Search Division Orders"
    />
  );
};

export { EntityDivisionOrders };
